import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';
import DtrOfficeTable from '../../components/DtrOfficeTable.jsx';
import DailyReportView from '../../components/DailyReportView.jsx';
import SafetyIncidents from '../../components/SafetyIncidents.jsx';

export default function FleetReportsPage() {
  const [ports, setPorts] = useState([]);
  const [portId, setPortId] = useState('');
  const [from, setFrom] = useState('2027-01-01');
  const [to, setTo] = useState('2027-12-31');
  const [thruster, setThruster] = useState(true);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);
  const [generating, setGenerating] = useState(false);

  const [metrics, setMetrics] = useState(null);
  const [period, setPeriod] = useState('month');
  const [hoursMovement, setHoursMovement] = useState(null);

  const loadMetrics = useCallback(async (p) => {
    const m = await api.getOperationsMetrics(p);
    setMetrics(m);
  }, []);

  const loadOptions = useCallback(async () => {
    const res = await api.getPpaReportOptions();
    setPorts(res?.ports || []);
    setPortId((prev) => prev || (res?.ports?.[0]?.id ?? ''));
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await loadOptions();
      } catch (err) {
        setLoadError(err.message || 'Failed to load report options.');
      } finally {
        setLoading(false);
      }
    })();
    // Dashboard metrics load independently so the PPA generator (the port
    // select) is ready as soon as the options arrive, without waiting on the
    // slower metrics aggregation.
    loadMetrics('month').catch(() => {});
    api.getHoursByMovement().then(setHoursMovement).catch(() => {});
  }, [loadOptions, loadMetrics]);

  const changePeriod = async (p) => {
    setPeriod(p);
    try {
      await loadMetrics(p);
    } catch {
      // keep prior metrics on a transient error
    }
  };

  const maxUtil = Math.max(1, ...(metrics?.utilization?.map((u) => u.utilizationPct) || [1]));
  const maxDelay = Math.max(1, ...(metrics?.delaysByReason?.map((d) => d.count) || [1]));

  const generate = async () => {
    if (!portId || !from || !to) {
      setPageMsg({ type: 'danger', text: 'Select a target port and a date range.' });
      return;
    }
    setGenerating(true);
    setPageMsg(null);
    try {
      const res = await api.generatePpaReport({ portId, from, to, thrusterFactorAllowed: thruster });
      setReport(res);
      setPageMsg({
        type: res.summary.nonCompliant > 0 ? 'warning' : 'success',
        text: `Report generated: ${res.summary.total} movement(s), ${res.summary.compliant} compliant, ${res.summary.nonCompliant} non-compliant.`,
      });
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to generate report.' });
    } finally {
      setGenerating(false);
    }
  };

  const downloadCsv = () => {
    if (!report?.csv) return;
    const portName = ports.find((p) => p.id === portId)?.name?.replace(/\s+/g, '-') || 'port';
    const blob = new Blob([report.csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ppa-compliance-${portName}-${from}_${to}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container-fluid py-4" data-testid="fleet-reports-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-print me-2" aria-hidden="true" />
                Fleet Performance &amp; PPA Compliance Reports
              </h3>
              <p className="text-muted mb-0 small">
                Generate the anticipatory regulatory PPA operations compliance report for filing.
              </p>
            </div>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="reports-load-error">
          {loadError}
        </div>
      )}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="reports-message">
          {pageMsg.text}
        </div>
      )}

      {/* Operations metrics dashboard (UC 06978c05) */}
      <div className="card border-0 shadow-sm mb-4" data-testid="ops-dashboard">
        <div className="card-header bg-white pt-4 border-bottom-0 d-flex justify-content-between align-items-center">
          <h5 className="mb-0 text-secondary">
            <i className="fas fa-chart-line me-2" aria-hidden="true" />
            Operations &amp; Fleet Utilization Dashboard
          </h5>
          <div className="btn-group btn-group-sm" role="group" aria-label="Period">
            {['week', 'month', 'quarter'].map((p) => (
              <button
                key={p}
                type="button"
                className={`btn ${period === p ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => changePeriod(p)}
                data-testid={`period-${p}`}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div className="card-body">
          {!metrics ? (
            <p className="text-muted small mb-0">Loading metrics…</p>
          ) : (
            <>
              {/* KPI cards */}
              <div className="row g-3 mb-4" data-testid="kpi-cards">
                <div className="col-6 col-lg-3">
                  <div className="border rounded p-3 h-100 bg-light text-center">
                    <div className="text-muted small text-uppercase">Avg Turnaround</div>
                    <div className="h4 mb-0 text-primary" data-testid="kpi-turnaround">
                      {metrics.avgTurnaroundHours}h
                    </div>
                  </div>
                </div>
                <div className="col-6 col-lg-3">
                  <div className="border rounded p-3 h-100 bg-light text-center">
                    <div className="text-muted small text-uppercase">On-time Performance</div>
                    <div className="h4 mb-0 text-success" data-testid="kpi-ontime">
                      {metrics.onTimePct}%
                    </div>
                  </div>
                </div>
                <div className="col-6 col-lg-3">
                  <div className="border rounded p-3 h-100 bg-light text-center">
                    <div className="text-muted small text-uppercase">Jobs Dispatched</div>
                    <div className="h4 mb-0 text-dark" data-testid="kpi-productivity">
                      {metrics.dispatcherProductivity.jobsDispatched}
                      <span className="text-muted small">
                        {' '}/ {metrics.dispatcherProductivity.jobsCompleted} done
                      </span>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-lg-3">
                  <div className="border rounded p-3 h-100 bg-light text-center">
                    <div className="text-muted small text-uppercase">Total Delays</div>
                    <div className="h4 mb-0 text-warning" data-testid="kpi-delays">
                      {metrics.totalDelays}
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                {/* Fleet utilization bar chart */}
                <div className="col-lg-6 mb-3">
                  <h6 className="text-secondary small text-uppercase">
                    Fleet Utilization ({metrics.period})
                  </h6>
                  <div data-testid="utilization-chart">
                    {metrics.utilization.length === 0 && (
                      <p className="text-muted small">No tugs to report.</p>
                    )}
                    {metrics.utilization.map((u) => (
                      <div key={u.tugId} className="mb-2" data-testid="util-bar" data-name={u.tugName}>
                        <div className="d-flex justify-content-between small">
                          <span>{u.tugName}</span>
                          <span className="fw-bold">{u.utilizationPct}%</span>
                        </div>
                        <div className="progress" style={{ height: 14 }}>
                          <div
                            className="progress-bar bg-primary"
                            role="progressbar"
                            style={{ width: `${(u.utilizationPct / maxUtil) * 100}%` }}
                            aria-valuenow={u.utilizationPct}
                            aria-valuemin={0}
                            aria-valuemax={100}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Delay reason breakdown */}
                <div className="col-lg-6 mb-3">
                  <h6 className="text-secondary small text-uppercase">Delays by Reason</h6>
                  <div data-testid="delay-chart">
                    {metrics.delaysByReason.length === 0 && (
                      <p className="text-muted small" data-testid="no-delays">
                        No delays logged.
                      </p>
                    )}
                    {metrics.delaysByReason.map((d) => (
                      <div key={d.reason} className="mb-2" data-testid="delay-bar" data-reason={d.reason}>
                        <div className="d-flex justify-content-between small">
                          <span>{d.reason}</span>
                          <span className="fw-bold">
                            {d.count} ({d.totalMinutes}m)
                          </span>
                        </div>
                        <div className="progress" style={{ height: 14 }}>
                          <div
                            className="progress-bar bg-warning"
                            role="progressbar"
                            style={{ width: `${(d.count / maxDelay) * 100}%` }}
                            aria-valuenow={d.count}
                            aria-valuemin={0}
                            aria-valuemax={maxDelay}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Chargeable hours by movement type (FR-RPT-3) */}
      <div className="row mb-4" data-testid="hours-by-movement">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white pt-4 border-bottom-0 d-flex justify-content-between align-items-center">
              <h5 className="mb-0 text-secondary">
                <i className="fas fa-stopwatch me-2 text-primary" aria-hidden="true" />
                Chargeable Hours by Movement Type
              </h5>
              {hoursMovement && (
                <span className="badge bg-light text-secondary border">
                  {hoursMovement.totalHours} h · {hoursMovement.totalJobs} jobs
                </span>
              )}
            </div>
            <div className="card-body">
              {!hoursMovement || hoursMovement.rows.length === 0 ? (
                <p className="text-muted small mb-0" data-testid="hours-empty">
                  No completed jobs with logged hours yet.
                </p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0 small" data-testid="hours-table">
                    <thead className="bg-light text-secondary text-uppercase">
                      <tr>
                        <th>Movement Type</th>
                        <th className="text-center">Jobs</th>
                        <th className="text-end">Total Hours</th>
                        <th className="text-end">Avg Hours/Job</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hoursMovement.rows.map((r) => (
                        <tr key={r.movementType} data-testid="hours-row">
                          <td className="fw-semibold text-dark">{r.movementType}</td>
                          <td className="text-center">{r.jobCount}</td>
                          <td className="text-end">{r.totalHours}</td>
                          <td className="text-end">{r.avgHours}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        {/* Generator form */}
        <div className="col-lg-4 mb-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white pt-4 border-bottom-0">
              <h5 className="mb-0 text-secondary">
                <i className="fas fa-file-csv me-2" aria-hidden="true" />
                Regulatory PPA Compliance Generator
              </h5>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="d-flex justify-content-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading…</span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mb-3">
                    <label htmlFor="rep-port" className="form-label small fw-bold text-muted">
                      Target Port <span className="text-danger">*</span>
                    </label>
                    <select
                      id="rep-port"
                      className="form-select"
                      value={portId}
                      onChange={(e) => setPortId(e.target.value)}
                      data-testid="report-port"
                    >
                      {ports.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="row">
                    <div className="col-6 mb-3">
                      <label htmlFor="rep-from" className="form-label small fw-bold text-muted">
                        From <span className="text-danger">*</span>
                      </label>
                      <input
                        id="rep-from"
                        type="date"
                        className="form-control"
                        value={from}
                        onChange={(e) => setFrom(e.target.value)}
                        data-testid="report-from"
                      />
                    </div>
                    <div className="col-6 mb-3">
                      <label htmlFor="rep-to" className="form-label small fw-bold text-muted">
                        To <span className="text-danger">*</span>
                      </label>
                      <input
                        id="rep-to"
                        type="date"
                        className="form-control"
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        data-testid="report-to"
                      />
                    </div>
                  </div>
                  <div className="form-check form-switch mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="rep-thruster"
                      checked={thruster}
                      onChange={(e) => setThruster(e.target.checked)}
                      data-testid="report-thruster"
                    />
                    <label className="form-check-label small" htmlFor="rep-thruster">
                      Thruster Deductible Factor Allowed
                    </label>
                  </div>
                  <button
                    className="btn btn-primary w-100"
                    onClick={generate}
                    disabled={generating || !portId}
                    data-testid="generate-report"
                  >
                    {generating && (
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    )}
                    <i className="fas fa-file-csv me-1" aria-hidden="true" />
                    Generate Regulatory Compliance CSV
                  </button>
                  {report && (
                    <button
                      className="btn btn-outline-success w-100 mt-2"
                      onClick={downloadCsv}
                      data-testid="download-csv"
                    >
                      <i className="fas fa-download me-1" aria-hidden="true" />
                      Download CSV
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="col-lg-8 mb-4">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white pt-4 border-bottom-0 d-flex justify-content-between align-items-center">
              <h5 className="mb-0 text-secondary">
                <i className="fas fa-clipboard-check me-2" aria-hidden="true" />
                PPA Operations Compliance
              </h5>
              {report && (
                <span
                  className={`badge p-2 ${report.summary.nonCompliant > 0 ? 'bg-danger' : 'bg-success'}`}
                  data-testid="report-summary"
                >
                  {report.summary.compliant}/{report.summary.total} compliant
                </span>
              )}
            </div>
            <div className="card-body p-0">
              {!report ? (
                <div className="text-center text-muted py-5" data-testid="no-report">
                  <i className="fas fa-file-invoice fa-3x mb-3 text-secondary" aria-hidden="true" />
                  <p className="small mb-0">Configure the parameters and generate the compliance report.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0" data-testid="report-table">
                    <thead className="bg-light text-secondary small text-uppercase">
                      <tr>
                        <th>Vessel / IMO</th>
                        <th>GT Band</th>
                        <th>Compulsory</th>
                        <th>Assisted</th>
                        <th>Thruster</th>
                        <th>Exemption</th>
                        <th>Compliance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.rows.length === 0 && (
                        <tr>
                          <td colSpan={7} className="text-center text-muted py-4" data-testid="report-empty">
                            No vessel movements for the selected port and date range.
                          </td>
                        </tr>
                      )}
                      {report.rows.map((r) => (
                        <tr
                          key={r.bookingRef}
                          data-testid="report-row"
                          data-ref={r.bookingRef}
                          data-compliant={r.compliant ? 'true' : 'false'}
                        >
                          <td className="small">
                            <strong>{r.vesselName}</strong>
                            <span className="d-block text-muted">IMO {r.imo}</span>
                          </td>
                          <td className="small">{r.gtBand}</td>
                          <td className="text-center" data-testid="report-compulsory">
                            {r.effectiveCompulsoryTugCount}
                          </td>
                          <td className="text-center" data-testid="report-assisted">
                            {r.assistedTugCount}
                          </td>
                          <td className="small">{r.thrusterAvailable ? 'Yes' : 'No'}</td>
                          <td className="small" data-testid="report-exemption">{r.exemptionStatus}</td>
                          <td>
                            {r.compliant ? (
                              <span className="badge bg-success">Compliant</span>
                            ) : (
                              <span className="badge bg-danger" data-testid="report-noncompliant">
                                <i className="fas fa-triangle-exclamation me-1" aria-hidden="true" />
                                Non-compliant
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-4">
        <DailyReportView />
      </div>
      <div className="mt-4">
        <SafetyIncidents />
      </div>
      <div className="mt-4">
        <DtrOfficeTable />
      </div>
    </div>
  );
}
